/**
 * Capability check helpers for the sub-admin RBAC system (Phase 4).
 * Mirrors backend/services/capabilities.py — keep the constants + CAP_IMPLIES in sync.
 *
 * - Super/full admins hold ["*"] which matches every capability.
 * - Sub-admins hold an explicit subset (e.g. ["manage_users", "host_classes"]).
 * - Regular users get [] and always fail.
 * - `manage_classes` implies `host_classes`. Helpers handle this so callers
 *   can just check the leaf cap.
 */

export const CAPS = {
  // Content
  MANAGE_CONTENT: 'manage_content',
  REVIEW_USER_SUBMISSIONS: 'review_user_submissions',
  MANAGE_BULK_IMPORT: 'manage_bulk_import',

  // User Management
  MANAGE_USERS: 'manage_users',
  CHANGE_USER_PLAN: 'change_user_plan',

  // Live Classes
  HOST_CLASSES: 'host_classes',
  MANAGE_CLASSES: 'manage_classes',

  // Finance / Analytics (read-only)
  VIEW_FINANCES: 'view_finances',
  VIEW_ANALYTICS: 'view_analytics',

  // Admin Tools sub-caps
  MANAGE_UPDATES: 'manage_updates',
  GRANT_COINS: 'grant_coins',
  VIEW_SESSIONS: 'view_sessions',
};

export const ALL_CAPS = Object.values(CAPS);

// Hierarchical relationships: holding the key cap grants everything in the value list.
export const CAP_IMPLIES = {
  [CAPS.MANAGE_CLASSES]: [CAPS.HOST_CLASSES],
};

const WILDCARD = '*';

const expandCaps = (perms) => {
  const out = new Set(perms);
  for (const cap of [...out]) {
    for (const implied of CAP_IMPLIES[cap] || []) {
      out.add(implied);
    }
  }
  return out;
};

export const hasCapability = (user, cap) => {
  const perms = user?.permissions || [];
  if (perms.includes(WILDCARD)) return true;
  return expandCaps(perms).has(cap);
};

export const hasAnyCapability = (user, caps) => {
  const perms = user?.permissions || [];
  if (perms.includes(WILDCARD)) return true;
  const expanded = expandCaps(perms);
  return caps.some((c) => expanded.has(c));
};

export const hasAllCapabilities = (user, caps) => {
  const perms = user?.permissions || [];
  if (perms.includes(WILDCARD)) return true;
  const expanded = expandCaps(perms);
  return caps.every((c) => expanded.has(c));
};
